import { useMutation } from '@apollo/client';
import { createError } from '@common/neo.tax';
import OneSchemaImporter, { FileUploadImportConfig } from '@oneschema/react';
import * as Sentry from '@sentry/react';
import React from 'react';

import {
  ADD_COMPANY_BULK_TICKETING_DATA_FILE,
  AddCompanyBulkTicketingDataFileMutation,
  AddCompanyBulkTicketingDataFileMutationVariables,
  FileUploadTicketingProvider,
  TicketingEntityType,
} from '@neo.tax/shared/graphql';
import { useCurrentUser } from '@neo.tax/shared/hooks';
import { env, isLocalEnvironment, resolveOneSchemaTemplateKey } from '@neo.tax/shared/utils';

import { TICKETING_PROVIDER_CONFIG } from '../ticketing-file-upload/ticketing-provider-config';

// OneSchema templates were registered in the OneSchema dashboard using the
// legacy `JIRA` enum value, before PLAT-293 split it into JIRA_CLOUD and
// JIRA_DATA_CENTER. Renaming the dashboard templates ahead of this PR shipping
// would break production (live `JIRA` template lookups would 404), so map the
// new JIRA_CLOUD enum back to the legacy `JIRA` prefix here. JIRA_DATA_CENTER
// was added separately (OPS-470) with its current enum value, so its templates
// already align.
// TODO: drop this remapping once the OneSchema dashboard templates are renamed
// to `JIRA_CLOUD-...`.
const getOneSchemaTemplateProviderName = (provider: FileUploadTicketingProvider): string => {
  if (provider === FileUploadTicketingProvider.JiraCloud) {
    return 'JIRA';
  }
  return provider;
};

type OneSchemaModalProps = {
  close: () => void;
  companyId: string;
  isOpen: boolean;
  oneSchemaJwt?: string;
  permanentUrl?: string;
  signedUrl?: string;
  ticketingEntityType?: TicketingEntityType;
  ticketingProvider?: FileUploadTicketingProvider;
  onLaunched?: () => void;
};

export const OneSchemaModal = ({
  close,
  companyId,
  isOpen,
  onLaunched,
  oneSchemaJwt,
  permanentUrl,
  signedUrl,
  ticketingEntityType,
  ticketingProvider,
}: OneSchemaModalProps): React.ReactElement => {
  const [addCompanyBulkTicketingDataFile] = useMutation<
    AddCompanyBulkTicketingDataFileMutation,
    AddCompanyBulkTicketingDataFileMutationVariables
  >(ADD_COMPANY_BULK_TICKETING_DATA_FILE);
  const { user } = useCurrentUser();

  if (!isOpen) {
    return <></>;
  }

  if (!oneSchemaJwt || !permanentUrl || !signedUrl || !ticketingEntityType || !ticketingProvider) {
    Sentry.addBreadcrumb({
      level: 'error',
      data: { user, oneSchemaJwt, permanentUrl, signedUrl, ticketingEntityType, ticketingProvider },
    });
    throw createError('OneSchemaModalError', 'Missing required parameters for OneSchemaModal');
  }

  const versionNumber = TICKETING_PROVIDER_CONFIG[ticketingProvider].versionNumber;
  const templateKey = `${getOneSchemaTemplateProviderName(ticketingProvider)}-${ticketingEntityType}${
    versionNumber ? `-V${versionNumber}` : ''
  }`;

  const onCancel = (): void => {
    close();
  };

  const onError = (error: unknown): void => {
    Sentry.addBreadcrumb({
      level: 'error',
      data: { ticketingProvider, ticketingEntityType },
    });
    throw error;
  };

  const onSuccess = async (): Promise<void> => {
    if (!permanentUrl || !ticketingProvider || !ticketingEntityType) {
      Sentry.addBreadcrumb({
        level: 'error',
        data: { permanentUrl, ticketingProvider, ticketingEntityType },
      });
      throw createError(
        'OneSchemaModalError',
        'Missing required parameters for calling addCompanyBulkTicketingDataFile',
      );
    }
    close();

    try {
      await addCompanyBulkTicketingDataFile({
        variables: {
          companyId,
          input: { permanentUrl, ticketingProvider, ticketingEntityType },
        },
      });
    } catch (error) {
      Sentry.addBreadcrumb({
        level: 'error',
        data: { error },
      });
      throw error;
    }
  };

  const importConfig: FileUploadImportConfig = {
    format: 'csv',
    type: 'file-upload',
    url: signedUrl ?? '',
  };

  return (
    <OneSchemaImporter
      /* managing state from your application */
      isOpen={isOpen}
      /* required config values */
      clientId={env.REACT_APP_ONE_SCHEMA_CLIENT_ID}
      userJwt={oneSchemaJwt ?? ''}
      templateKey={resolveOneSchemaTemplateKey(templateKey)}
      /* optional config values */
      importConfig={importConfig}
      devMode={isLocalEnvironment()}
      className="oneschema-importer"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1000,
      }}
      /* handling results */
      onCancel={onCancel}
      onError={onError}
      onSuccess={onSuccess}
      onLaunched={onLaunched}
    />
  );
};
